// frontend/src/components/auth/GoogleAuthButton.js

/**
 * GoogleAuthButton
 *
 * Renders the official "Sign in with Google" button from @react-oauth/google,
 * wrapped in a loading/error-aware container that matches the app's design system.
 *
 * Props:
 *   onSuccess(idToken: string)  — called with the raw Google ID token on success
 *   onError(message: string)    — called with a user-facing error message on failure
 *   loading (bool)              — disables the button and shows a spinner overlay
 *   disabled (bool)             — caller-owned gate (the sign-up terms gate, via
 *                                 components/auth/TermsGatedGoogleDoor.js).
 *                                 Without it, this container's `pointerEvents:"auto"`
 *                                 overrode a parent's `pointer-events:none`.
 *                                 Does not dim — the caller owns that affordance.
 *   isHebrew (bool)             — switches button locale (he / en)
 *
 * Security note:
 *   The `credential` returned by GoogleLogin IS the Google ID token (JWT).
 *   It is forwarded as-is to the backend for server-side verification.
 *   The frontend never inspects or trusts the token contents.
 *
 * WHY <GoogleOAuthProvider> IS MOUNTED HERE, AND MUST NOT BE HOISTED (#119A)
 *
 *   The provider does not just supply context. @react-oauth/google's
 *   `useLoadGsiScript` appends
 *
 *       <script src="https://accounts.google.com/gsi/client">
 *
 *   to document.body from an effect that runs on PROVIDER MOUNT - not on
 *   button render, not on click, and with no condition attached. So the
 *   provider's position in the tree IS the answer to "which pages contact
 *   Google".
 *
 *   It used to wrap the whole app in App.js, above <Router>. That loaded
 *   Google Identity Services on the homepage, the blog, /privacy, /terms and
 *   /cookies - for visitors who will never sign in, and before the cookie
 *   banner can gate anything. Worse, scripts/prerender.js serialises the
 *   appended tag into the static HTML, so all 30 prerendered routes SHIPPED
 *   it and it loaded before React even booted. Each of those page views sends
 *   the visitor's IP, user-agent and origin Referer to Google - and a classic
 *   <script src> is a credentialed request, so a visitor signed in to Google
 *   sends their Google cookies with it. None of that is disclosed in any
 *   policy we publish. Structurally the same defect as the Google Fonts one
 *   closed by #110, on higher traffic.
 *
 *   Mounting the provider HERE scopes the script to the surfaces that
 *   actually offer Google sign-in - only /login, /register and the conversion
 *   popup's step 2 - because this component is the only thing that renders
 *   it. The popup's step 2 mounts only after the visitor taps "Start now", so
 *   no page loads the script on page load except /login and /register. One Tap
 *   is off (useOneTap={false} below), so there is no reason at all for the
 *   script to exist on a page with no button.
 *
 *   DO NOT HOIST IT. Not back to App.js, not to AuthLayout, not into a
 *   "providers" barrel. If a third page ever needs Google sign-in, render THIS
 *   component there - do not lift the provider to cover both. Two independent
 *   guards enforce that:
 *     - a grep guard in .github/workflows/lint.yml (PR gate) that allowlists
 *       this one file, and
 *     - an assertion in frontend/scripts/prerender.js (deploy gate) on the
 *       written HTML of every prerendered route: accounts.google.com must
 *       appear on /login and /register and NOWHERE else (the popup's step 2
 *       is never in a snapshot - it needs a tap to mount).
 *
 *   ONE BUTTON PER PAGE - and this is a correctness rule, not tidiness.
 *   Co-locating the provider here does make a second one likelier than a single
 *   app-wide provider did, and the consequence is worse than the duplicated
 *   script tag it looks like. GoogleLogin's effect calls
 *   `window.google.accounts.id.initialize({ client_id, callback })`, and that
 *   is a GLOBAL SINGLETON: a second mounted GoogleLogin REPLACES the first's
 *   callback. Google then delivers the credential from EITHER button to
 *   whichever mounted last. Two buttons in different modes - say a sign-in and
 *   a sign-up on one page - would send the wrong `mode` to POST /auth/google,
 *   so a new user clicking "sign up" gets a 404 "no account is linked to this
 *   Google email" instead of an account. Silent, and it would look like a
 *   backend bug.
 *
 *   Not reachable today (Login and Register are separate routes and each render
 *   exactly one, and the conversion popup never shows on an auth route), and
 *   not a regression this change introduces - the singleton behaves
 *   identically under one app-wide provider. It is written down because
 *   the fix for it is "render one button", which is not obvious from the
 *   symptom.
 */

import React, { useRef, useState, useLayoutEffect } from "react";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { Box, CircularProgress, Typography } from "@mui/material";
import useHydrated from "../../motion/useHydrated";

// The height of GSI's "large" button: @react-oauth/google gives GoogleLogin's
// container exactly this (its containerHeightMap.large), and the slot below
// reserves it before the button exists, so the button arriving one render
// after hydration moves nothing.
const GSI_LARGE_BUTTON_HEIGHT = 40;

/**
 * True while scripts/prerender.js is taking a page's snapshot (it sets
 * window.__PRERENDER__ before the app boots). False in a visitor's browser.
 */
function isPrerenderSnapshot() {
  try {
    return typeof window !== "undefined" && window.__PRERENDER__ === true;
  } catch {
    return false;
  }
}

// Read at module scope, exactly as App.js did before #119A. The value lives in
// the tracked frontend/.env.production; commit bad47ad0 ("restore Google SSO
// client_id in production frontend build") is what happens when it goes missing
// from a build, so treat this line as load-bearing.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

// Copy for the script-load-failure notice, per surface and per language.
//
// THE TWO VARIANTS ARE NOT DUPLICATION - do not collapse them. They differ by a
// single word, and that word is the whole point: this component renders on
// /login, /register and the conversion popup's step 2, and the sentence has to
// name the action the visitor can actually take on the surface they are on (the
// popup is a sign-up surface, so it uses the register wording). On a sign-up
// surface there may be no account and no existing password yet, so telling
// them to "log in" (התחבר / sign in) with email and password is advice they
// cannot follow.
//
// BOTH halves of the sentence change with the mode, which is the owner's wording
// and the point of the split: on /register the Google button signs you UP, so the
// subject is הרשמה ("registration"), not התחברות ("signing in"), and the fallback
// it points at is הירשם ("sign up"), not התחבר ("log in"). Naming the wrong action
// twice on the register page is what this prop exists to prevent - do not collapse
// the two entries back into one.
//
// Everything else is deliberate: the Hebrew carries NO trailing full stop (his
// text, verbatim), the English does, matching the other user-facing message
// strings in this file. ASCII hyphen only.
const SCRIPT_ERROR_TEXT = {
  login: {
    he: "התחברות עם גוגל אינה זמינה כרגע - התחבר עם מייל וסיסמה מטה",
    en: "Google sign-in is unavailable right now - sign in with email and password below.",
  },
  register: {
    he: "הרשמה עם גוגל אינה זמינה כרגע - הירשם עם מייל וסיסמה מטה",
    en: "Google sign-up is unavailable right now - sign up with email and password below.",
  },
};

/**
 * @param {Object}   props
 * @param {Function} props.onSuccess  - Receives the raw ID token string
 * @param {Function} props.onError    - Receives a human-readable error string
 * @param {boolean}  [props.loading]  - Shows spinner and blocks interaction
 * @param {boolean}  [props.disabled] - Caller-owned gate. Defaults false, so non-gating
 *                                      callers (Login.js) are unaffected.
 * @param {boolean}  [props.isHebrew] - Switches locale to Hebrew
 * @param {"login"|"register"} [props.mode] - Which surface this is rendered on.
 *                                      Selects the script-failure copy (see
 *                                      SCRIPT_ERROR_TEXT). Defaults to "login",
 *                                      so an omitted prop is safe rather than
 *                                      undefined.
 *
 * WHY `mode` EXISTS, since nothing else here needs it. This component renders on
 * /login, /register and the conversion popup's step 2, and until it was added
 * nothing told it which. The
 * two call sites are otherwise near-identical: Register additionally passes
 * `disabled`, but that tracks its terms checkbox and is false on both surfaces
 * once the box is ticked, so it was never usable as a context signal. The
 * login/register distinction otherwise lives only in the PARENTS, each calling
 * loginWithGoogle(idToken, "login" | "register") from its own handler - a fact
 * the component could not see.
 *
 * It drives COPY ONLY. It must not be used to change the auth call, which stays
 * the parents' business, and it does not touch `text="continue_with"` on
 * GoogleLogin - that stays hardcoded and identical on both surfaces, because
 * Google's own label ("להמשיך עם Google") is deliberately neutral between
 * signing in and signing up.
 */
const GoogleAuthButton = ({
  onSuccess,
  onError,
  loading = false,
  disabled = false,
  isHebrew = true,
  mode = "login",
}) => {
  const containerRef = useRef(null);
  // Cap at 400 (Google's max); shrink to fit the container on small screens
  const [buttonWidth, setButtonWidth] = useState(400);
  // The Google Identity script failed to load, so there is no button to click.
  // Cleared on a successful (re)load - see onScriptLoadSuccess below, which
  // matters because the provider remounts on every language toggle.
  const [scriptFailed, setScriptFailed] = useState(false);
  // Drives both the provider's ?hl= script param and the remount key below.
  const gsiLocale = isHebrew ? "he" : "en";
  // #464: Google's button exists only from the first post-hydration render.
  // Google fills GoogleLogin's container with its own DOM (a div and a
  // cross-origin iframe); the prerendered /login and /register snapshots used
  // to carry that DOM, and React cannot hydrate an element it never rendered -
  // React 19 regenerated the whole page over it. So the hydration pass renders
  // the slot EMPTY, scripts/prerender.js empties the same slot in the snapshot
  // (data-prerender-empty), and the two match. On a client-only boot (a route
  // change, the conversion popup) `hydrated` is true from the first render
  // and nothing changes. The ToS gate is untouched: the gating callers wrap
  // this whole component (TermsGatedGoogleDoor), and while the slot is empty
  // there is nothing to activate at all.
  const hydrated = useHydrated();

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setButtonWidth(Math.min(el.offsetWidth, 400));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * Handle successful Google sign-in.
   * credentialResponse.credential is the raw ID token — pass it straight to the caller.
   */
  const handleSuccess = (credentialResponse) => {
    // `disabled` must mean "cannot sign anyone in", not just "hard to click".
    if (disabled) return;
    if (!credentialResponse?.credential) {
      onError(isHebrew ? "לא התקבל אסימון מגוגל. נסה שוב." : "No token received from Google. Please try again.");
      return;
    }
    onSuccess(credentialResponse.credential);
  };

  /**
   * Handle Google sign-in failure.
   * The library calls this on popup-closed, network errors, or cancelled flows.
   */
  const handleError = () => {
    onError(
      isHebrew
        ? "ההתחברות עם גוגל נכשלה. נסה שוב."
        : "Google sign-in failed. Please try again."
    );
  };

  /**
   * The Google Identity SCRIPT failed to load. NOT a sign-in failure.
   *
   * Without this the failure is completely silent: GoogleLogin renders an empty
   * container until `scriptLoadedSuccessfully` flips true, so a failed load
   * leaves a blank 40px gap with no message and nothing to explain it.
   *
   * It became reachable more often when the provider moved in here (#119A): it
   * now remounts on every language toggle, and `?hl=he` / `?hl=en` are different
   * URLs with different ~260KB bodies - a real fetch, not a cache hit - so a
   * flaky mobile connection can fail the reload and blank a button that worked a
   * second earlier. Previously the provider mounted once at app boot and a
   * toggle could not re-trigger it.
   *
   * THREE deliberate choices about how this surfaces:
   *
   *   1. Its own passive string, NOT handleError's. That copy says the sign-in
   *      "failed" and to try again - it asserts an attempt that never happened,
   *      and "try again" cannot help when the cause is an ad blocker. This says
   *      the option is unavailable and points at the email form below it, which
   *      is the action that actually works.
   *   2. Rendered INLINE below the button, not pushed through `onError`. That
   *      prop drives the page-level Alert used for real login and registration
   *      failures; putting a passive, page-load-time notice there would show a
   *      red error to every uBlock/Brave/Firefox-strict visitor before they had
   *      done anything. On a sign-in page that is not a rare population.
   *   3. Recorded unconditionally but DISPLAYED only when enabled. An early
   *      `if (disabled) return` here would be a trap: on /register the script
   *      can fail while the terms box is still unticked, and the message would
   *      then never appear at all - the user ticks the box and gets the silent
   *      blank button this whole handler exists to prevent. Gating the render
   *      instead gives the same outcome while disabled and the right one after.
   */
  const handleScriptLoadError = () => {
    setScriptFailed(true);
  };

  // #464: the notice is the VISITOR's browser's news, never the snapshot's. The
  // prerender is a client render too - Google's script loads there, and when
  // it fails at build time the notice would be baked into the /login and
  // /register HTML, where it does not match the hydration pass (which can
  // never have a failure yet) and would tell every visitor Google is down. So
  // it never renders while the snapshot is taken, and never on the hydration
  // pass; a failure in the visitor's browser shows it from then on. The live
  // region itself stays mounted throughout (see below) - only its text and
  // its margin depend on this.
  const showScriptNotice = hydrated && !isPrerenderSnapshot() && scriptFailed && !disabled;

  return (
    <Box sx={{ width: "100%" }}>
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: 44,
        // Never force "auto" when gated — that descendant override is what
        // defeated the parent's `pointer-events: none`.
        pointerEvents: loading || disabled ? "none" : "auto",
        opacity: loading ? 0.6 : 1,
        transition: "opacity 0.2s ease",
      }}
      role="region"
      aria-label={isHebrew ? "כניסה עם גוגל" : "Sign in with Google"}
    >
      {/* Google's official button — width is measured from the container and capped at 400.
          The provider wraps ONLY this subtree: see the header comment.

          `locale` belongs on the PROVIDER, not on GoogleLogin. GoogleLogin does
          not destructure it (see the library's dist/index.esm.js), so a `locale`
          prop there falls into `...props` and is forwarded to
          `google.accounts.id.initialize()`, which has no such config field. The
          prop was on GoogleLogin here and was therefore dead; it has been
          dropped rather than left in place, so the next reader is not told by
          the code that it works there.

          THE SCRIPT URL IS THE ONLY MECHANISM. The provider's `locale` does two
          things: it appends ?hl=<locale> to the gsi/client URL, and it flows
          through context into renderButton's config. It is tempting to assume
          the second is what localises the button — it is not. Measured against
          the real Google endpoint, React blocked, one variable at a time:

            script ?hl=he                      -> "להמשיך עם Google"
            script ?hl=en                      -> "Continue with Google"
            no ?hl, <html lang> he OR en       -> Hebrew from an Israeli IP,
                                                  English from a US CI runner
                                                  (geolocation; lang is ignored)
            no ?hl + renderButton locale "en"  -> still Hebrew
            ?hl=en + renderButton locale "he"  -> English
            ?hl=he + renderButton locale "en"  -> Hebrew

          So renderButton's locale never won a single head-to-head, and never
          appeared in the button iframe's URL at all. ?hl decides; do not "fix"
          this by putting locale back on GoogleLogin.

          `key` is load-bearing. The provider's context useMemo does NOT list
          `locale` in its deps, and the script effect keys only on `nonce`, so a
          locale change alone would never reach Google. Navbar's language toggle
          flips `isHebrew` in place with no remount, so without the key the page
          would switch languages around a button frozen in the old one. Keying on
          the locale remounts the provider, which reloads the script with the new
          ?hl and re-renders the button — verified to switch both ways in a live
          page. It costs a remount ONLY on a language toggle.

          THE SLOT (#464) reserves the button's exact box - full width, GSI's
          40px - whether or not the button is in it yet, and is the node
          prerender empties (see `hydrated` above). No role, no tabindex, no
          aria-hidden: empty, it is not focusable and announces nothing, and
          aria-hidden would hide the real button from assistive tech once it
          is inside. */}
      <Box
        data-prerender-empty="google-button"
        sx={{
          width: "100%",
          height: GSI_LARGE_BUTTON_HEIGHT,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {hydrated && (
          <GoogleOAuthProvider
            key={gsiLocale}
            clientId={GOOGLE_CLIENT_ID}
            locale={gsiLocale}
            onScriptLoadError={handleScriptLoadError}
            // Clears a previous failure. Load-bearing because the provider remounts
            // on every language toggle: without it, one failed ?hl=he fetch would
            // leave the notice on screen underneath a button that reloaded fine as
            // ?hl=en.
            onScriptLoadSuccess={() => setScriptFailed(false)}
          >
            <GoogleLogin
              onSuccess={handleSuccess}
              onError={handleError}
              useOneTap={false}
              size="large"
              shape="pill"
              theme="outline"
              text="continue_with"
              width={buttonWidth}
            />
          </GoogleOAuthProvider>
        )}
      </Box>

      {/* Spinner overlay while the API request is in-flight */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-live="polite"
          aria-label={isHebrew ? "מתחבר…" : "Signing in…"}
        >
          <CircularProgress size={20} sx={{ color: "#00A896" }} />
        </Box>
      )}
    </Box>

    {/* Script-load notice. Sits OUTSIDE the button's region and below it, so
        the button's own reserved box (minHeight 44 / the 40px GSI container) is
        untouched and nothing above it moves - only content below shifts by one
        line, the same trade TermsGatedGoogleDoor's terms hint already makes.

        ALWAYS MOUNTED with its text toggled, rather than conditionally
        rendered: that registers the live region before its content changes,
        which is what makes the announcement reliable.

        Rendered only when not `disabled` - see handleScriptLoadError - and
        never in the prerender snapshot - see showScriptNotice. #64748B
        not a lighter grey because this text is the only explanation for a
        button that is not there, so it has to clear contrast.

        COPY IS THE OWNER'S, verbatim - the Hebrew has no trailing full stop on
        purpose, do not "correct" it. The English keeps its full stop to match
        the two other user-facing message strings in this file (the aria-labels
        have none; they are labels, not sentences).

        The text is chosen by `mode`, which is why SCRIPT_ERROR_TEXT has two
        near-identical entries - see the note there before merging them. The
        `|| SCRIPT_ERROR_TEXT.login` fallback covers a typo'd mode value: the
        default parameter only helps when the prop is OMITTED, and indexing an
        unknown key would otherwise throw while rendering an error notice. */}
    <Typography
      role="status"
      aria-live="polite"
      sx={{
        mt: showScriptNotice ? 1 : 0,
        fontSize: "12px",
        color: "#64748B",
        textAlign: "center",
      }}
    >
      {showScriptNotice
        ? (SCRIPT_ERROR_TEXT[mode] || SCRIPT_ERROR_TEXT.login)[isHebrew ? "he" : "en"]
        : ""}
    </Typography>
    </Box>
  );
};

export default GoogleAuthButton;
